/*
 * This file contains utility functions that can be used in multiple components.
*/

import { ISensitivityLabelExpanded } from "../interfaces/i-sensitivity-label";
import { Signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { debounceTime } from "rxjs";
import { DEFAULT_SENSITIVITY_LABEL, PUBLIC_LABEL_CODE } from "../constants/sensitivity.constants";

/**
 * Checks if the given text contains a URL.
 */
export function containsUrl(text: string): boolean {
  const urlPattern = /https?:\/\/[^\s/$.?#].[^\s]*/gi;
  return urlPattern.test(text);
}

/**
 * Wraps any URLs within the text with an anchor element and the target attribute.
 */
export function wrapUrlsWithAnchor(text: string): string {
  const urlPattern = /https?:\/\/[^\s/$.?#].[^\s]*/gi;
  const anchorPattern = /<a\s+[^>]*href="https?:\/\/[^\s/$.?#].[^\s]*"[^>]*>.*?<\/a>/gi;

  // Remove existing anchor tags
  text = text.replace(anchorPattern, (anchor) => {
    const urlMatch = anchor.match(urlPattern);
    return urlMatch ? urlMatch[0] : anchor;
  });

  // Add new anchor tags
  return text.replace(urlPattern, (url) => {
    return `<a href="${url}" target="_blank">${url}</a>`;
  });
}

/**
 * Gets the default sensitivity label (Public) from the list of sensitivity labels.
 * Falls back to `DEFAULT_SENSITIVITY_LABEL` from the canonical flat list if Public is not present.
 * @param sensitivityLabel - Array of expanded labels to search.
 */
export function getDefaultSensitivityLabel(sensitivityLabel: ISensitivityLabelExpanded[]): ISensitivityLabelExpanded {
  return sensitivityLabel.find(label => label.id === PUBLIC_LABEL_CODE) ?? DEFAULT_SENSITIVITY_LABEL;
}

export function debounceSignal<T>(signal: Signal<T>, time: number): Signal<T | undefined>  {
  let debouncedObservable$ = toObservable(signal).pipe(
    debounceTime(time)
  );
  return toSignal(debouncedObservable$);
}
